import {useAppSelector, useModal} from '@hooks';
import {useTranslation} from 'react-i18next';
import {StyledModalPage, CardWrapperEmpty} from './styles';
import {ModalCell, Button} from '@molecules';
import {FlexCol, P} from '@quarks';
import {Illustration, Loader} from '@atoms';
import {LicensePlatesFormModalPage} from '../LicensePlateFormModalPage/LicensePlatesFormModalPage';
import {getLicensePlates, getIsUpdatingParkingPreferences} from '@lib/store';
import {pxToRem} from '@utils';
import {LicensePlateItem} from './LicensePlateItem';
import {HTMLAttributes} from 'react';

export type Props = HTMLAttributes<HTMLDivElement> & {
  buildingId?: string;
  date?: string;
};

export const LicensePlatesModalPage = ({buildingId, date}: Props) => {
  const {t} = useTranslation();
  const {closeModal, addPages} = useModal();

  const licensePlates = useAppSelector(getLicensePlates);
  const isLicensePlatesLoading = useAppSelector(getIsUpdatingParkingPreferences);

  const hasLicensePlates = !!licensePlates?.length;

  const illustration = hasLicensePlates ? 'error' : 'car';

  const ariaLabel = hasLicensePlates ? 'error' : t('profile:LicensePlate.emptyPageAriaLabel');

  const emptyStateTitle = t('profile:LicensePlate.emptyPageTitle');
  const emptyStateLabel = t('profile:LicensePlate.emptyPageDescription');
  const buttonLabel = t('profile:LicensePlate.addLicensePlate');

  const MAX_LICENSE_PLATE_COUNT = 10;
  const reachedLimit = licensePlates.length >= MAX_LICENSE_PLATE_COUNT;

  const addLicensePlate = () => {
    addPages([
      <LicensePlatesFormModalPage
        date={date}
        buildingId={buildingId}
      />,
    ]);
  };

  return (
    <StyledModalPage
      title={date ? t('parking:BookParkingSpot') : t('profile:LicensePlate.LicensePlate_other')}
      onClose={closeModal}>
      <ModalCell>
        {isLicensePlatesLoading && !hasLicensePlates ? (
          <CardWrapperEmpty>
            <Loader></Loader>
          </CardWrapperEmpty>
        ) : hasLicensePlates ? (
          <>
            <FlexCol rowGap={16}>
              <span>
                {licensePlates.map((licensePlate) => (
                  <LicensePlateItem
                    key={licensePlate}
                    licensePlate={licensePlate}
                    buildingId={buildingId}
                  />
                ))}
              </span>
              {reachedLimit && (
                <P>{t('profile:LicensePlate.maxLicensePlateReached', {limit: MAX_LICENSE_PLATE_COUNT})}</P>
              )}
              <div>
                <Button
                  disabled={reachedLimit}
                  data-testid="organisms-profile-LicensePlatePage-Add-LicensePlate-button-fs"
                  button="tertiary"
                  onClick={addLicensePlate}
                  iconRight="plus"
                  size="small">
                  {buttonLabel}
                </Button>
              </div>
            </FlexCol>
          </>
        ) : (
          <CardWrapperEmpty>
            <Illustration
              illustration={illustration}
              size="100px"
              aria-label={ariaLabel}
            />
            <FlexCol
              rowGap={16}
              alignItems="center">
              <FlexCol
                rowGap={8}
                maxWidth={pxToRem(320)}>
                {emptyStateTitle && <b>{emptyStateTitle}</b>}
                <P>{emptyStateLabel}</P>
              </FlexCol>
              <div>
                <Button
                  data-testid="organisms-profile-LicensePlatePage-Add-LicensePlate-button-fs"
                  button="primary"
                  onClick={addLicensePlate}
                  iconRight="plus"
                  size="small">
                  {buttonLabel}
                </Button>
              </div>
            </FlexCol>
          </CardWrapperEmpty>
        )}
      </ModalCell>
    </StyledModalPage>
  );
};
